import { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getAbout } from '../lib/api';

const AboutPage = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await getAbout();
        setAbout(res.data);
      } catch (error) {
        console.error('Error fetching about:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAbout();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4">Who We Are</p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-tight text-neutral-900 mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>{about?.title || 'About Us'}</h1>
          {about?.subtitle && (
            <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto px-4">{about.subtitle}</p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Image */}
            <div className="overflow-hidden order-1 lg:order-none">
              {about?.image_data ? (
                <img 
                  src={about.image_data} 
                  alt="About"
                  className="w-full aspect-[4/3] object-cover"
                />
              ) : (
                <img 
                  src="https://images.unsplash.com/photo-1628709489643-13788c76c864?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxkaiUyMHBlcmZvcm1pbmclMjBlbGVnYW50JTIwd2hpdGUlMjBwYXJ0eSUyMGNyb3dkfGVufDB8fHx8MTc3MzA3NjMzM3ww&ixlib=rb-4.1.0&q=85" 
                  alt="About"
                  className="w-full aspect-[4/3] object-cover"
                />
              )}
            </div>
            
            {/* Text */}
            <div className="order-2 lg:order-none">
              <div className="prose prose-lg max-w-none">
                {about?.content ? (
                  <div className="text-sm sm:text-base leading-relaxed text-neutral-500 whitespace-pre-wrap">{about.content}</div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    <p className="text-sm sm:text-base leading-relaxed text-neutral-500">
                      Welcome to our DJ services! We are passionate about creating unforgettable experiences 
                      through music. With years of experience in the industry, we've had the privilege of 
                      being part of countless celebrations, weddings, corporate events, and parties.
                    </p>
                    <p className="text-sm sm:text-base leading-relaxed text-neutral-500">
                      Our mission is simple: to provide professional, reliable, and entertaining DJ services 
                      that exceed your expectations. We believe that the right music can transform any event 
                      from ordinary to extraordinary.
                    </p>
                    <p className="text-sm sm:text-base leading-relaxed text-neutral-500">
                      Whether you're planning an intimate gathering or a large-scale celebration, we have 
                      the expertise and equipment to make your event a success. Let us help you create 
                      memories that will last a lifetime.
                    </p>
                  </div>
                )}
              </div>
              
              <a href="/contact" className="bg-neutral-900 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg shadow-neutral-900/20 inline-block mt-6 sm:mt-8 text-sm sm:text-base">
                Get In Touch
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
            <div>
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-neutral-900 mb-1 sm:mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>500+</p>
              <p className="text-neutral-500 text-sm sm:text-base">Events</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-neutral-900 mb-1 sm:mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>10+</p>
              <p className="text-neutral-500 text-sm sm:text-base">Years Experience</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-neutral-900 mb-1 sm:mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>100%</p>
              <p className="text-neutral-500 text-sm sm:text-base">Satisfaction</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-neutral-900 mb-1 sm:mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>5</p>
              <p className="text-neutral-500 text-sm sm:text-base">Star Rating</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
